import React, { useEffect, useRef } from 'react';
import { FaGithub, FaLinkedin, FaTwitter, FaAws, FaDocker, FaRocket, FaDownload, FaCode, FaCloud, FaServer } from 'react-icons/fa';
import { SiKubernetes, SiTerraform, SiJenkins, SiAnsible, SiPrometheus, SiGrafana, SiPython, SiGo } from 'react-icons/si';
import { TypeAnimation } from 'react-type-animation';

const Hero = () => {
  const containerRef = useRef(null);

  const techIcons = [
    { icon: <FaAws className="h-10 w-10" />, name: 'AWS', color: 'from-orange-500 to-yellow-500' },
    { icon: <FaDocker className="h-10 w-10" />, name: 'Docker', color: 'from-blue-400 to-blue-600' },
    { icon: <SiKubernetes className="h-10 w-10" />, name: 'K8s', color: 'from-blue-500 to-cyan-500' },
    { icon: <SiTerraform className="h-10 w-10" />, name: 'Terraform', color: 'from-purple-500 to-pink-500' },
    { icon: <SiJenkins className="h-10 w-10" />, name: 'Jenkins', color: 'from-red-500 to-pink-500' },
    { icon: <SiAnsible className="h-10 w-10" />, name: 'Ansible', color: 'from-gray-700 to-gray-900' },
    { icon: <SiPython className="h-10 w-10" />, name: 'Python', color: 'from-yellow-500 to-blue-500' },
    { icon: <SiGo className="h-10 w-10" />, name: 'Go', color: 'from-cyan-500 to-blue-500' },
    { icon: <SiPrometheus className="h-10 w-10" />, name: 'Prometheus', color: 'from-red-400 to-orange-500' },
    { icon: <SiGrafana className="h-10 w-10" />, name: 'Grafana', color: 'from-orange-500 to-purple-500' },
  ];

  useEffect(() => {
    const handleMouseMove = (e) => {
      const container = containerRef.current;
      if (!container) return;

      const { left, top, width, height } = container.getBoundingClientRect();
      const x = ((e.clientX - left) / width) * 100;
      const y = ((e.clientY - top) / height) * 100;

      container.style.setProperty('--mouse-x', `${x}%`);
      container.style.setProperty('--mouse-y', `${y}%`);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleDownloadCV = () => {
    const link = document.createElement('a');
    link.href = '/resume.pdf';
    link.download = 'Tehseen_Khan_DevOps_Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section 
      id="home" 
      ref={containerRef}
      className="min-h-screen flex items-center relative overflow-hidden"
      style={{
        background: 'radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(59, 130, 246, 0.1) 0%, transparent 50%), linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
      }}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-blue-900/20 to-cyan-900/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-gradient-to-r from-cyan-900/20 to-purple-900/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-gradient-to-r from-purple-900/20 to-blue-900/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(59,130,246,0.05)_50%,transparent_75%,transparent_100%)] bg-[length:20px_20px] opacity-20"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8 animate-fade-in">
            <div className="space-y-6">
              {/* Availability Badge */}
              <div className="inline-flex mt-10 items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-blue-900/30 to-cyan-900/30 backdrop-blur-sm border border-blue-700/30 text-cyan-400 text-sm font-medium animate-pulse">
                <div className="w-2 h-2 bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full animate-ping"></div>
                <FaCloud className="mr-2" />
                <TypeAnimation
                  sequence={[
                    'Open to Opportunities',
                    2000,
                    'DevOps Engineer',
                    2000,
                    'Cloud Specialist',
                    2000,
                    'SRE Practitioner',
                    2000,
                  ]}
                  wrapper="span"
                  speed={50}
                  repeat={Infinity}
                  className="font-semibold"
                />
              </div>
              
              {/* Main Heading */}
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="block text-gray-100 animate-slide-up">
                  Building Cloud
                </span>
                <span className="block bg-gradient-to-r from-blue-500 via-cyan-400 to-purple-500 bg-clip-text text-transparent animate-gradient">
                  Infrastructure
                </span>
                <span className="block text-gray-300 text-4xl md:text-5xl mt-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
                  That Just Works
                </span>
              </h1>
              
              {/* Description */}
              <p className="text-xl text-gray-400 leading-relaxed max-w-2xl animate-slide-up" style={{ animationDelay: '0.4s' }}>
                Hi, I'm <span className="font-semibold text-blue-400">Tehseen Nayeem Khan</span>. 
                I specialize in creating scalable, reliable cloud infrastructure using 
                <span className="font-semibold text-purple-400"> AWS, Kubernetes, and Terraform</span>. 
                Passionate about automation and solving complex production challenges.
              </p>
            </div>

            {/* Buttons */}
            <div className="flex flex-wrap gap-4 animate-slide-up" style={{ animationDelay: '0.6s' }}>
              <button 
                onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
                className="group relative overflow-hidden bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-8 py-3.5 rounded-xl font-semibold hover:shadow-2xl transition-all duration-300 shadow-lg hover:shadow-blue-500/25 transform hover:-translate-y-1"
              >
                <span className="relative z-10 flex items-center">
                  <FaRocket className="mr-2 group-hover:rotate-12 transition-transform duration-300" />
                  View Projects
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 transform translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
              </button>
              
              <button 
                onClick={handleContact}
                className="group relative overflow-hidden border-2 border-blue-600 text-blue-400 px-8 py-3.5 rounded-xl font-semibold hover:text-white transition-all duration-300 transform hover:-translate-y-1"
              >
                <span className="relative z-10 flex items-center">
                  <FaCode className="mr-2" />
                  Contact Me
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
              </button>

              <button 
                onClick={handleDownloadCV}
                className="group relative overflow-hidden bg-gradient-to-r from-gray-800 to-gray-900 text-white px-8 py-3.5 rounded-xl font-semibold hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <span className="relative z-10 flex items-center">
                  <FaDownload className="mr-2 group-hover:animate-bounce" />
                  Download CV
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-gray-900 to-gray-800 transform translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
              </button>
            </div>

            {/* Tech Stack Preview */}
            <div className="pt-8 animate-slide-up" style={{ animationDelay: '0.8s' }}>
              <p className="text-sm text-gray-500 mb-4 font-medium">TECH STACK</p>
              <div className="flex flex-wrap gap-3">
                {techIcons.slice(0, 6).map((tech, index) => (
                  <div
                    key={index}
                    className="group relative overflow-hidden bg-gray-900/80 backdrop-blur-sm rounded-xl p-3 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-800"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${tech.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                    <div className="relative z-10 flex flex-col items-center">
                      <div className="text-2xl mb-1 transform group-hover:scale-110 transition-transform duration-300">
                        {tech.icon}
                      </div>
                      <span className="text-xs font-medium text-gray-400 group-hover:text-gray-200 transition-colors duration-300">
                        {tech.name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-6 pt-6 animate-slide-up" style={{ animationDelay: '1s' }}>
              {[
                { icon: <FaGithub className="h-6 w-6" />, label: 'GitHub', color: 'hover:text-white', bgColor: 'hover:bg-gray-800' },
                { icon: <FaLinkedin className="h-6 w-6" />, label: 'LinkedIn', color: 'hover:text-blue-400', bgColor: 'hover:bg-blue-900/30' },
                { icon: <FaTwitter className="h-6 w-6" />, label: 'Twitter', color: 'hover:text-sky-400', bgColor: 'hover:bg-sky-900/30' },
              ].map((social, index) => (
                <a
                  key={index}
                  href="#"
                  className={`group relative p-3 bg-gray-900/80 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-800 ${social.color} ${social.bgColor}`}
                >
                  <div className="text-gray-400 group-hover:scale-110 transition-all duration-300">
                    {social.icon}
                  </div>
                  <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap text-xs font-medium text-gray-300 bg-gray-900 px-2 py-1 rounded shadow border border-gray-700">
                    {social.label}
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Right Content - Animated Profile */}
          <div className="relative animate-fade-in" style={{ animationDelay: '0.3s' }}>
            <div className="relative w-80 h-80 md:w-[500px] md:h-[500px] mx-auto">
              {/* Outer animated ring */}
              <div className="absolute inset-0 animate-spin-slow">
                <div className="absolute inset-[-10px] bg-gradient-to-r from-blue-600 via-cyan-500 to-purple-600 rounded-full blur-xl opacity-30"></div>
              </div>
              
              {/* Main profile container */}
              <div className="relative w-full h-full bg-gradient-to-br from-gray-900 to-gray-950 rounded-full shadow-2xl overflow-hidden border-8 border-gray-900">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-cyan-500/10 to-purple-500/10"></div>
                
                <div className="relative w-full h-full flex items-center justify-center p-8">
                  <div className="text-center space-y-6">
                    {/* Profile image/initials */}
                    <div className="relative group">
                      <div className="w-64 h-64 mx-auto bg-gradient-to-br from-blue-600 via-cyan-500 to-purple-600 rounded-full shadow-2xl flex items-center justify-center transform group-hover:scale-105 transition-transform duration-500">
                        <div className="text-white text-center p-8">
                          <div className="text-6xl font-bold mb-4">TNK</div>
                          <div className="space-y-2">
                            <div className="text-2xl font-bold">Tehseen Nayeem Khan</div>
                            <div className="text-cyan-200 font-semibold">DevOps Engineer</div>
                            <div className="text-sm text-blue-100 mt-4 space-y-1">
                              <div className="flex items-center justify-center gap-2">
                                <FaServer className="animate-pulse" />
                                Cloud Infrastructure
                              </div>
                              <div className="flex items-center justify-center gap-2">
                                <FaCode className="animate-pulse" />
                                Automation Expert
                              </div>
                              <div className="flex items-center justify-center gap-2">
                                <FaCloud className="animate-pulse" />
                                Production Support
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full blur opacity-30 group-hover:opacity-50 transition-opacity duration-500"></div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { value: '3+', label: 'Years Exp' },
                        { value: '50+', label: 'Projects' },
                        { value: '99.9%', label: 'Uptime' },
                      ].map((stat, index) => (
                        <div key={index} className="bg-gray-900/80 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-gray-800">
                          <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                            {stat.value}
                          </div>
                          <div className="text-xs text-gray-400">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating tech badges */}
              {[
                { icon: <FaAws className="h-8 w-8 text-orange-500" />, position: 'top-4 right-4', delay: '0' },
                { icon: <SiKubernetes className="h-8 w-8 text-blue-400" />, position: 'bottom-4 left-4', delay: '1000' },
                { icon: <FaDocker className="h-8 w-8 text-blue-500" />, position: 'top-1/2 -left-8', delay: '2000' },
                { icon: <SiTerraform className="h-8 w-8 text-purple-400" />, position: 'top-1/2 -right-8', delay: '3000' },
              ].map((badge, index) => (
                <div
                  key={index}
                  className={`absolute ${badge.position} transform animate-float`}
                  style={{ animationDelay: `${badge.delay}ms` }}
                >
                  <div className="w-14 h-14 bg-gray-900 rounded-2xl shadow-2xl flex items-center justify-center transform hover:scale-110 transition-transform duration-300 border-2 border-gray-800">
                    {badge.icon}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="flex flex-col items-center text-cyan-400">
          <div className="text-sm mb-2">Scroll</div>
          <div className="w-6 h-10 border-2 border-cyan-600 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-cyan-500 rounded-full mt-2 animate-scroll"></div>
          </div>
        </div>
      </div>

      {/* Custom styles */}
      <style jsx>{`
        @keyframes blob {
          0%, 100% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes scroll {
          0% {
            transform: translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateY(20px);
            opacity: 0;
          }
        }
        
        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
        
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
        
        .animate-slide-up {
          animation: slide-up 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-scroll {
          animation: scroll 2s infinite;
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </section>
  );
};

export default Hero;